// organicViews.js

import { isSystemTypeView } from 'services/views/TypeViewService';
import { isBoardGroupableField } from 'utilities/NodelistField';
import { isSchemaFieldShownInDefaultLists } from 'utilities/schemaFieldVisibility';
import { isCatchAllView } from 'utilities/ViewRows';

export const CATCH_ALL_FALLBACK_VIEW_TYPE = 'LIST';

export const DEFAULT_CATCH_ALL_VIEW_TYPES = Object.freeze([
    'LIST',
    'ALPHA_LIST',
    'TABLE',
    'GALLERY',
    'FEED',
    'CALENDAR',
    'MAP',
]);

export const ORGANIC_CATCH_ALL_VIEW_TYPES = Object.freeze([
    ...DEFAULT_CATCH_ALL_VIEW_TYPES,
    'BOARD',
]);

export const CATCH_ALL_VIEW_TYPE_MEMORY_STORAGE_KEY = 'nodal:catchAllViewTypeByDatabaseScope:v1';
export const CATCH_ALL_BOARD_FIELD_MEMORY_STORAGE_KEY = 'nodal:catchAllBoardFieldByScope:v1';
export const CATCH_ALL_PROPERTIES_MEMORY_STORAGE_KEY = 'nodal:catchAllPropertiesByScopeType:v1';
export const CATCH_ALL_CALENDAR_FIELD_MEMORY_STORAGE_KEY = 'nodal:catchAllCalendarFieldByScope:v1';

const FIELD_CHOOSER_VIEW_TYPES = new Set([
    'LIST',
    'ALPHA_LIST',
    'TABLE',
    'GALLERY',
    'FEED',
    'BOARD',
    'CALENDAR',
]);

const EXCLUDED_FIELD_CHOOSER_KEYS = new Set(['title']);

const BASE_CATCH_ALL_ALLOWED_VIEW_TYPES = new Set(DEFAULT_CATCH_ALL_VIEW_TYPES);
const ORGANIC_CATCH_ALL_ALLOWED_VIEW_TYPES = new Set(ORGANIC_CATCH_ALL_VIEW_TYPES);

/**
 * Whether layout mutations may be written to the view document.
 * Catch-all / synthetic "all" views must stay ephemeral.
 *
 * @param {object|null|undefined} view
 * @returns {boolean}
 */
export function canPersistViewLayout(view) {
    if (!view) return false;
    if (isCatchAllView(view)) return false;
    if (isSystemTypeView(view)) return false;
    const viewKey = String(view?.key || view?.id || '').trim();
    return Boolean(viewKey) && viewKey.toLowerCase() !== 'all';
}

/**
 * Normalizes a catch-all display type against the allowed set for the surface.
 *
 * @param {string} type
 * @param {object} [options]
 * @param {boolean} [options.organicEnabled=false]
 * @param {boolean} [options.allowBoard=true]
 * @param {string} [options.fallback]
 * @returns {string}
 */
export function normalizeCatchAllViewType(type, {
    organicEnabled = false,
    allowBoard = true,
    fallback = CATCH_ALL_FALLBACK_VIEW_TYPE,
} = {}) {
    const normalizedType = String(type || '').trim().toUpperCase();
    const allowed = organicEnabled
        ? ORGANIC_CATCH_ALL_ALLOWED_VIEW_TYPES
        : BASE_CATCH_ALL_ALLOWED_VIEW_TYPES;

    if (!allowed.has(normalizedType)) return fallback;
    if (normalizedType === 'BOARD' && (!organicEnabled || !allowBoard)) return fallback;
    return normalizedType;
}

/**
 * Resolves the catch-all type list for a toolbar surface.
 *
 * @param {object} [options]
 * @param {boolean} [options.organicEnabled=false]
 * @param {boolean} [options.allowBoard=false]
 * @returns {string[]}
 */
export function getCatchAllViewTypes({
    organicEnabled = false,
    allowBoard = false,
} = {}) {
    if (!organicEnabled) return [...DEFAULT_CATCH_ALL_VIEW_TYPES];
    if (!allowBoard) return [...DEFAULT_CATCH_ALL_VIEW_TYPES];
    return [...ORGANIC_CATCH_ALL_VIEW_TYPES];
}

/**
 * Builds board-capable field choices for organic Board group-by.
 *
 * @param {Array} resolvedProperties
 * @returns {Array<{ key: string, label: string, type: string }>}
 */
export function buildCatchAllBoardFields(resolvedProperties) {
    return (Array.isArray(resolvedProperties) ? resolvedProperties : [])
        .filter((field) => isBoardGroupableField(field))
        .map((field) => {
            const key = String(field?.name || field?.key || '').trim();
            if (!key) return null;

            const label = String(field?.label || key).trim() || key;
            const type = String(field?.type || '').trim().toLowerCase();

            return {
                key,
                label,
                type,
            };
        })
        .filter(Boolean);
}

/**
 * Picks a valid board field from memory or the first available field.
 *
 * @param {string|null|undefined} preferredFieldKey
 * @param {Array<{ key: string }>} boardFields
 * @returns {string}
 */
export function resolveCatchAllBoardField(preferredFieldKey, boardFields) {
    const fields = Array.isArray(boardFields) ? boardFields : [];
    const preferred = String(preferredFieldKey || '').trim();
    if (preferred && fields.some((field) => field?.key === preferred)) {
        return preferred;
    }
    return String(fields[0]?.key || '').trim();
}

/**
 * Whether the active catch-all type supports the field chooser.
 *
 * @param {string} viewType
 * @returns {boolean}
 */
export function catchAllViewTypeSupportsFieldChooser(viewType) {
    return FIELD_CHOOSER_VIEW_TYPES.has(String(viewType || '').trim().toUpperCase());
}

/**
 * Builds checklist options for the organic field chooser.
 *
 * @param {Array} resolvedProperties
 * @returns {Array<{ key: string, label: string, type: string, displayInDefaultLists: boolean }>}
 */
export function buildCatchAllPropertyFields(resolvedProperties) {
    return (Array.isArray(resolvedProperties) ? resolvedProperties : [])
        .filter((field) => {
            if (field?.hidden === true) return false;
            const key = String(field?.name || field?.key || '').trim();
            if (!key) return false;
            return !EXCLUDED_FIELD_CHOOSER_KEYS.has(key.toLowerCase());
        })
        .map((field) => {
            const key = String(field?.name || field?.key || '').trim();
            return {
                key,
                label: String(field?.label || key).trim() || key,
                type: String(field?.type || '').trim().toLowerCase(),
                displayInDefaultLists: isSchemaFieldShownInDefaultLists(field),
            };
        });
}

/**
 * Default catch-all field keys from the type schema opt-in flag.
 *
 * @param {Array<{ key?: string, displayInDefaultLists?: boolean }>} propertyFields
 * @returns {string[]}
 */
export function getCatchAllDefaultPropertyKeys(propertyFields) {
    return (Array.isArray(propertyFields) ? propertyFields : [])
        .filter((field) => isSchemaFieldShownInDefaultLists(field))
        .map((field) => String(field?.key || '').trim())
        .filter(Boolean);
}

/**
 * Normalizes a stored properties selection against current field options.
 * Missing/null stored value uses schema default-list fields (opt-in).
 * An explicit array is kept even when it is empty or contains every field.
 *
 * @param {string[]|null|undefined} storedProperties
 * @param {Array<{ key: string, displayInDefaultLists?: boolean }>} propertyFields
 * @returns {string[]|null}
 */
export function resolveCatchAllProperties(storedProperties, propertyFields) {
    const availableKeys = (Array.isArray(propertyFields) ? propertyFields : [])
        .map((field) => String(field?.key || '').trim())
        .filter(Boolean);

    if (!availableKeys.length) return null;

    if (!Array.isArray(storedProperties)) {
        const availableSet = new Set(availableKeys);
        return getCatchAllDefaultPropertyKeys(propertyFields)
            .filter((key) => availableSet.has(key));
    }

    const availableSet = new Set(availableKeys);
    const selected = [];
    const seen = new Set();

    storedProperties.forEach((key) => {
        const normalized = String(key || '').trim();
        if (!normalized || !availableSet.has(normalized) || seen.has(normalized)) return;
        seen.add(normalized);
        selected.push(normalized);
    });

    return selected;
}

/**
 * Builds the preference map key for properties (scope + view type).
 *
 * @param {string} scopeKey
 * @param {string} viewType
 * @returns {string}
 */
export function getCatchAllPropertiesPreferenceKey(scopeKey, viewType) {
    const scope = String(scopeKey || '').trim() || '__scope__';
    const type = String(viewType || '').trim().toUpperCase() || CATCH_ALL_FALLBACK_VIEW_TYPE;
    return `${scope}::${type}`;
}

/**
 * Resolves a remembered calendar field against available temporal fields.
 *
 * @param {string|null|undefined} preferredFieldKey
 * @param {Array<{ key: string, name?: string }>} calendarFields
 * @returns {string}
 */
export function resolveCatchAllCalendarField(preferredFieldKey, calendarFields) {
    const fields = Array.isArray(calendarFields) ? calendarFields : [];
    const preferred = String(preferredFieldKey || '').trim();
    const keys = fields
        .map((field) => String(field?.name || field?.key || '').trim())
        .filter(Boolean);

    if (preferred && keys.includes(preferred)) return preferred;
    return keys[0] || '';
}

/**
 * Reads a scoped string map from localStorage.
 *
 * @param {string} storageKey
 * @returns {object}
 */
export function readScopedPreferenceMap(storageKey) {
    if (typeof window === 'undefined' || !window?.localStorage) return {};

    try {
        const raw = window.localStorage.getItem(storageKey);
        const parsed = raw ? JSON.parse(raw) : {};
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
}

/**
 * Writes a scoped string map to localStorage.
 *
 * @param {string} storageKey
 * @param {object} nextMap
 * @returns {void}
 */
export function writeScopedPreferenceMap(storageKey, nextMap) {
    if (typeof window === 'undefined' || !window?.localStorage) return;

    try {
        window.localStorage.setItem(storageKey, JSON.stringify(nextMap || {}));
    } catch {
        // Ignore storage failures; in-memory state should still work.
    }
}

/**
 * Maps organic lens selections into persisted view filter rows.
 *
 * @param {object} lensSelections
 * @param {Array<{ key: string, kind?: string }>} lensFields
 * @returns {Array<object>}
 */
export function buildFiltersFromLensSelections(lensSelections, lensFields) {
    const fieldsByKey = new Map(
        (Array.isArray(lensFields) ? lensFields : [])
            .filter((field) => field?.key)
            .map((field) => [field.key, field]),
    );

    return Object.entries(lensSelections || {})
        .map(([fieldKey, selectedValue]) => {
            const normalizedFieldKey = String(fieldKey || '').trim();
            const normalizedValue = String(selectedValue || '').trim();
            if (!normalizedFieldKey || !normalizedValue) return null;

            const field = fieldsByKey.get(normalizedFieldKey);
            const kind = String(field?.kind || field?.type || 'enum').trim().toLowerCase();

            if (kind === 'tags' || kind === 'multiselect') {
                return {
                    field: normalizedFieldKey,
                    operator: 'is_any_of',
                    value: [normalizedValue],
                    unit: null,
                    type: kind,
                    joinToNext: 'and',
                };
            }

            return {
                field: normalizedFieldKey,
                operator: 'is',
                value: normalizedValue,
                unit: null,
                type: kind === 'enum' ? 'enum' : kind,
                joinToNext: 'and',
            };
        })
        .filter(Boolean);
}

/**
 * Suggests a title for an organic Save View action.
 *
 * @param {object} params
 * @param {string} params.viewType
 * @param {string} [params.boardFieldLabel]
 * @param {number} [params.filterCount]
 * @returns {string}
 */
export function suggestOrganicViewTitle({
    viewType,
    boardFieldLabel = '',
    filterCount = 0,
} = {}) {
    const typeLabel = String(viewType || '').trim();
    const displayType = typeLabel
        ? typeLabel.charAt(0) + typeLabel.slice(1).toLowerCase()
        : 'View';
    const groupLabel = String(boardFieldLabel || '').trim();

    if (typeLabel === 'BOARD' && groupLabel) {
        return filterCount > 0
            ? `${groupLabel} Board · filtered`
            : `${groupLabel} Board`;
    }

    if (filterCount > 0) {
        return `${displayType} · filtered`;
    }

    return displayType;
}
